/**
 * ApexChat — chat surface for the global Apex drawer.
 *
 * This is a refactor of the relevant slice of ChatPanel.tsx tailored to the
 * Apex use case. ChatPanel.tsx is left untouched because it's still the
 * canonical chat for the public agency/vendor intake pages.
 *
 * Key differences vs ChatPanel:
 *  - Subscribes to ApexProvider for messages + send/cancel callbacks.
 *  - Approval card uses the slice 9 contract: `summary` (bold heading) +
 *    `reasoning` (collapsible, expanded by default if non-empty). Raw tool
 *    args are never shown to the user.
 *  - On Cancel, sends `{cancelled: true}` to /aiHandler so the backend can
 *    run a final ack turn.
 *  - Empty state shows greeting + 3 quick-prompt chips.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  App,
  Badge,
  Button,
  Card,
  Collapse,
  Space,
  Typography,
} from "antd";
import type { UploadFile } from "antd";
import { CloudUploadOutlined, PaperClipOutlined } from "@ant-design/icons";
import { Attachments, Bubble, Sender } from "@ant-design/x";
import ReactMarkdown from "react-markdown";
import { markdownComponents } from "../MarkdownLink";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { APP_BRAND_NAME } from "../../lib/brand";
import { ditColors } from "../../lib/theme";
import {
  useApex,
  type ApexConfirmation,
  type ApexMessage,
  type UploadedFile,
} from "./ApexProvider";

const QUICK_PROMPTS = [
  "What's at risk this week?",
  "Show me intakes pending review",
  "Summarize my projects",
];

const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;

export default function ApexChat() {
  const { messages, sendMessage, cancelConfirmation, sending } = useApex();
  const { message } = App.useApp();

  const [inputValue, setInputValue] = useState("");
  const [headerOpen, setHeaderOpen] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<UploadFile[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const messagesRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll on new messages.
  useEffect(() => {
    const container = messagesRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [messages, sending]);

  // ---------------- File upload (copied from ChatPanel) ----------------

  const handleFileChange = useCallback(
    async (info: { fileList: UploadFile[] }) => {
      let next = info.fileList;
      if (next.length > MAX_FILES) {
        message.error(`Limit ${MAX_FILES} files per turn.`);
        next = next.slice(0, MAX_FILES);
      }
      setAttachedFiles(next);

      const processed: UploadedFile[] = [];
      for (const fileItem of next) {
        const file = fileItem.originFileObj;
        if (!file) continue;

        const fileType = file.type;
        const fileName = file.name.toLowerCase();
        const isSupported =
          fileType.startsWith("image/") ||
          fileType === "application/pdf" ||
          fileType === "text/plain" ||
          fileType === "text/markdown" ||
          fileType === "text/csv" ||
          fileType ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
          fileName.endsWith(".pdf") ||
          fileName.endsWith(".txt") ||
          fileName.endsWith(".md") ||
          fileName.endsWith(".csv") ||
          fileName.endsWith(".docx");

        if (!isSupported) {
          message.error(`File type not supported: ${file.name}`);
          continue;
        }
        if (file.size > MAX_BYTES) {
          message.error(`${file.name} exceeds 10 MB limit.`);
          continue;
        }

        try {
          const content = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              if (typeof reader.result === "string") {
                const dataUrl = reader.result;
                const parts = dataUrl.split(",");
                resolve(parts.length === 2 ? parts[1] : dataUrl);
                return;
              }
              const buffer = reader.result as ArrayBuffer;
              const bytes = new Uint8Array(buffer);
              let binary = "";
              for (let i = 0; i < bytes.length; i += 1) {
                binary += String.fromCharCode(bytes[i]);
              }
              resolve(btoa(binary));
            };
            reader.onerror = reject;
            if (fileType.startsWith("image/")) {
              reader.readAsDataURL(file);
            } else {
              reader.readAsArrayBuffer(file);
            }
          });

          processed.push({
            uid: fileItem.uid,
            file,
            content,
            type: fileType || "application/octet-stream",
          });
        } catch (err) {
          console.error(`Error reading file ${file.name}:`, err);
          message.error(`Failed to read file: ${file.name}`);
        }
      }
      setUploadedFiles(processed);
    },
    [message]
  );

  // ---------------- Send + confirmation handlers ----------------

  const handleSend = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed && uploadedFiles.length === 0) return;
      const filesToSend = uploadedFiles.length > 0 ? uploadedFiles : undefined;
      setInputValue("");
      setAttachedFiles([]);
      setUploadedFiles([]);
      setHeaderOpen(false);
      await sendMessage(trimmed, { files: filesToSend });
    },
    [sendMessage, uploadedFiles]
  );

  const handleConfirm = useCallback(
    async (confirmation: ApexConfirmation) => {
      // Strip the card from the message list immediately so the user sees progress.
      // The provider's sendMessage will append a new assistant turn.
      await sendMessage("", {
        confirmedTool: { name: confirmation.tool, args: confirmation.args },
      });
    },
    [sendMessage]
  );

  const handleCancel = useCallback(
    async (confirmation: ApexConfirmation) => {
      await cancelConfirmation(confirmation);
    },
    [cancelConfirmation]
  );

  const handleQuickPrompt = useCallback(
    (text: string) => {
      void handleSend(text);
    },
    [handleSend]
  );

  // ---------------- Sender header (attachments) ----------------

  const attachmentsNode = (
    <Badge dot={attachedFiles.length > 0 && !headerOpen}>
      <Button
        type="text"
        icon={<PaperClipOutlined aria-hidden />}
        aria-label="Attach files"
        onClick={() => setHeaderOpen(!headerOpen)}
      />
    </Badge>
  );

  const senderHeader = (
    <Sender.Header
      title="Attachments"
      open={headerOpen}
      onOpenChange={setHeaderOpen}
      styles={{ content: { padding: 0 } }}
    >
      <Attachments
        beforeUpload={() => false}
        items={attachedFiles}
        onChange={handleFileChange}
        accept=".pdf,.docx,.txt,.md,.csv,image/png,image/jpeg,image/webp"
        placeholder={(type) =>
          type === "drop"
            ? { title: "Drop file here" }
            : {
                icon: <CloudUploadOutlined aria-hidden />,
                title: "Upload files",
                description: "Click or drag files to this area to upload",
              }
        }
      />
    </Sender.Header>
  );

  // ---------------- Bubble.List items ----------------

  const bubbleItems = useMemo(
    () =>
      messages.map((msg, index) => ({
        key: `apex-msg-${index}`,
        role: msg.role === "user" ? ("user" as const) : ("ai" as const),
        content: msg.text,
        streaming: Boolean(msg.streaming),
        extraInfo: {
          attachments: msg.attachments,
          thoughts: msg.thoughts,
          confirmation: msg.confirmation,
        },
      })),
    [messages]
  );

  const isEmpty = messages.length === 0;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        width: "100%",
        height: "100%",
      }}
    >
      <div
        ref={messagesRef}
        role="log"
        aria-live="polite"
        aria-label="Apex chat messages"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          paddingRight: 4,
        }}
      >
        {isEmpty ? (
          <Card style={{ borderColor: ditColors.secondaryLight }}>
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <Typography.Title level={5} style={{ marginBottom: 0 }}>
                Hi, I'm {APP_BRAND_NAME}. What can I help with?
              </Typography.Title>
              <Typography.Text type="secondary">
                Try one of these to get started:
              </Typography.Text>
              <Space direction="vertical" size={6} style={{ width: "100%" }}>
                {QUICK_PROMPTS.map((qp) => (
                  <Button
                    key={qp}
                    type="default"
                    style={{ textAlign: "left", whiteSpace: "normal", height: "auto" }}
                    block
                    onClick={() => handleQuickPrompt(qp)}
                  >
                    {qp}
                  </Button>
                ))}
              </Space>
            </Space>
          </Card>
        ) : (
          <Bubble.List
            role={{
              ai: {
                placement: "start",
                contentRender: (content, info) => {
                  const extra = info?.extraInfo as
                    | {
                        attachments?: UploadFile[];
                        thoughts?: string;
                        confirmation?: ApexMessage["confirmation"];
                      }
                    | undefined;
                  return (
                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                      {extra?.attachments?.length ? (
                        <Attachments
                          items={extra.attachments}
                          disabled
                          styles={{ upload: { display: "none" } }}
                        />
                      ) : null}
                      {extra?.thoughts ? (
                        <Typography.Text
                          style={{
                            display: "block",
                            fontSize: 12,
                            fontStyle: "italic",
                            color: "rgba(0, 0, 0, 0.45)",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {extra.thoughts}
                        </Typography.Text>
                      ) : null}
                      {extra?.confirmation ? (
                        <ApexApprovalCard
                          confirmation={extra.confirmation}
                          onConfirm={handleConfirm}
                          onCancel={handleCancel}
                          loading={sending}
                        />
                      ) : null}
                      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
                        {String(content ?? "")}
                      </ReactMarkdown>
                    </Space>
                  );
                },
                styles: { content: { borderRadius: 16 } },
              },
              user: {
                placement: "end",
                variant: "shadow",
                contentRender: (content, info) => {
                  const attachments = (info?.extraInfo as { attachments?: UploadFile[] } | undefined)
                    ?.attachments;
                  return (
                    <Space direction="vertical" size={8}>
                      {attachments?.length ? (
                        <Attachments
                          items={attachments}
                          disabled
                          styles={{ upload: { display: "none" } }}
                        />
                      ) : null}
                      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
                        {String(content ?? "")}
                      </ReactMarkdown>
                    </Space>
                  );
                },
                styles: { content: { borderRadius: 16 } },
              },
            }}
            items={bubbleItems}
          />
        )}
      </div>

      <Sender
        value={inputValue}
        onChange={setInputValue}
        placeholder={`Ask ${APP_BRAND_NAME} anything...`}
        header={senderHeader}
        onSubmit={handleSend}
        loading={sending}
        prefix={attachmentsNode}
      />
    </div>
  );
}

interface ApprovalCardProps {
  confirmation: ApexConfirmation;
  onConfirm: (confirmation: ApexConfirmation) => Promise<void> | void;
  onCancel: (confirmation: ApexConfirmation) => Promise<void> | void;
  loading?: boolean;
}

function ApexApprovalCard({ confirmation, onConfirm, onCancel, loading }: ApprovalCardProps) {
  // Default-expand "Why?" when the model gave us a non-empty rationale.
  const defaultActiveKey = confirmation.reasoning?.trim() ? ["why"] : [];

  return (
    <Card
      size="small"
      style={{
        maxWidth: 520,
        borderColor: ditColors.primary,
        background: "#fff",
      }}
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Typography.Text strong>{confirmation.summary}</Typography.Text>

        {confirmation.reasoning ? (
          <Collapse
            ghost
            size="small"
            defaultActiveKey={defaultActiveKey}
            items={[
              {
                key: "why",
                label: "Why?",
                children: (
                  <Typography.Paragraph
                    style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}
                  >
                    {confirmation.reasoning}
                  </Typography.Paragraph>
                ),
              },
            ]}
          />
        ) : null}

        <Space>
          <Button
            type="primary"
            size="small"
            loading={loading}
            onClick={() => onConfirm(confirmation)}
            style={{ background: ditColors.primary, borderColor: ditColors.primary }}
          >
            Confirm
          </Button>
          <Button size="small" onClick={() => onCancel(confirmation)} disabled={loading}>
            Cancel
          </Button>
        </Space>
      </Space>
    </Card>
  );
}
